import { useEffect, useState } from 'react'
import { apiGet } from '../generic/Api_Functions'
import SmartNavigator from '../generic/SmartNavigator'
import { SmartSaveDialog } from '../generic/utilities/SmartDialog'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { Button } from '@mui/material'
import PartDateDialogContent from './PartDateDialogContent'
import PinJobEventDialog from './PinJobEventDialog'
import JobSettingsDialogContent from './JobSettingsDialogContent'
import { EVENT_DIALOG_TABS, EVENT_DIALOG_TAB_KEYS, getEventDialogTabKey } from './utils/eventDialogTabs'

/**
* @typedef {import("../generic/utilities/objectStructures/planner/event").Event} Event Event object
* @typedef {import("../generic/utilities/objectStructures/mos/ReportQuantity").ReportQuantity} ReportQuantity ReportQuantity object
* @typedef {import("../generic/utilities/objectStructures/mos/Worker").Worker} Worker Worker object
* @typedef {import("../generic/utilities/objectStructures/mos/FabricationOrder").FabricationOrder} FabricationOrder Fabrication Order object
* @typedef {import("../generic/utilities/objectStructures/planner/planification").Planification} Planification Planification object
* @typedef {import("../generic/utilities/objectStructures/planner/event").PinnedSettings} PinnedSettings PinnedSettings object
* @typedef {import("../generic/utilities/objectStructures/planner/event").Job} Job Job object
* @typedef {import("../generic/utilities/objectStructures/planner/jobSettings").JobSettings} JobSettings JobSettings object
**/

/**
 * Planner event dialog tabs.
 *
 * Story 96 — risk R3: every decision in this component is taken on the tab's NAME, never on its
 * position. The new "Job Settings" tab goes FIRST, which shifted every index this file used to
 * switch on; `getEventDialogTabKey` converts once, at the SmartNavigator boundary, and the rest of
 * the file reads as what it means.
 *
 * @param {object} props Props object
 * @param {Event} props.event Current selected event
 * @param {React.Dispatch<React.SetStateAction<Event>>} props.setEvent Current selected event useState setter
 * @param {number} props.selectedTab Current selected tab index
 * @param {React.Dispatch<React.SetStateAction<number>>} props.onChangeSelectedTab Function to handle tab change
 * @param {function(boolean)} props.onAccept Function to handle accept event
 * @param {Planification} props.currentPlanification Current planification object
 * @param {Job} props.newPinJobSettings New pin Job settings object
 * @param {function(Job)} props.onChangeNewPinJobSettings Function to handle new pin job settings change
 * @param {React.ReactElement} props.AlertElementDialog Alert element dialog
 * @param {PinnedSettings} props.pinSettings Pin settings object
 * @param {function(PinnedSettings)} props.onChangePinSettings Function to handle pin settings change
 * @param {JobSettings} props.jobSettings Job settings currently stored for the job
 * @param {JobSettings} props.newJobSettings Job settings being edited
 * @param {function(JobSettings)} props.onChangeNewJobSettings Function to handle job settings change
 * @param {boolean} props.showJobSettingsErrors Whether the job settings validation errors are shown
 * @returns
 */
const EventDialog = ({
  event,
  setEvent,
  selectedTab,
  onChangeSelectedTab,
  onAccept,
  currentPlanification,
  newPinJobSettings,
  onChangeNewPinJobSettings,
  AlertElementDialog,
  pinSettings,
  onChangePinSettings,
  jobSettings,
  newJobSettings,
  onChangeNewJobSettings,
  showJobSettingsErrors,
  setResourceFamily,
  setIsFamilyViewActive,
  setOpenDialog
}) => {
  const { t_ } = useSmartTranslation()
  const [showSaveDialog, setshowSaveDialog] = useState(false)
  const [nextTab, setNextTab] = useState(0)
  const selectedTabKey = getEventDialogTabKey(selectedTab)

  useEffect(() => {
    const getPinSettings = async () => {
      let data
      if (currentPlanification.unitCriteria.name === 'Fabrication Order') {
        data = await apiGet('planner/pinSettings/FO/' + currentPlanification.id + '/' + event.fabricationOrderId + '/' + event.jobReferenceId + '/' + event.taskId)
      } else {
        data = await apiGet('planner/pinSettings/' + currentPlanification.id + '/' + event.partId + '/' + event.taskId)
      }
      const updatedData = {
        ...data,
        currentJob: {
          ...data.currentJob,
          oldMachineName: data.currentJob.machineName
        }
      }
      onChangePinSettings(updatedData)
      onChangeNewPinJobSettings(updatedData.currentJob)
    }
    // S82/R3: the load is driven by the tab's NAME, never by its position. Only the pin tab needs it:
    // the "Job Settings" tab shows no date, and the draft relocation after a machine change is
    // resolved by Sun inside the job settings write itself (it takes `planificationId` + `taskId`), so
    // this side never has to assemble a plan-save body.
    if (!isNaN(event.partId) && !isNaN(event.taskId) && !isNaN(currentPlanification?.id) && selectedTabKey === EVENT_DIALOG_TAB_KEYS.PIN_SETTING) {
      getPinSettings()
    }
  }, [selectedTabKey, event, currentPlanification, onChangeNewPinJobSettings, onChangePinSettings])

  const handleTempCloseAndChangeColor = async () => {
    try {
      const isJobMode = currentPlanification.unitCriteria.name === 'Job'
      const url = 'planner/getTempPlanificationTaskFamily/' + currentPlanification.id + (isJobMode ? ('/byPart/' + event.partId) : ('/byFo/' + event.fabricationOrderId))
      const resourceFamily = await apiGet(url)
      setResourceFamily(resourceFamily)
      setIsFamilyViewActive(true)
    } catch (error) {
      console.error('Error fetching resource family:', error)
    }
    setOpenDialog(false)
  }

  // S29/S83/RN35/E8: leaving a tab with unsaved changes asks for confirmation. Each tab reports
  // whether it is dirty; the guard itself is the same for the three, and is resolved by the NAME of
  // the tab being left, never by its position.
  const isTabDirty = {
    [EVENT_DIALOG_TAB_KEYS.JOB_SETTINGS]: () => {
      if (!jobSettings || !newJobSettings) {
        return false
      }
      return String(newJobSettings.jobPriority ?? '') !== String(jobSettings.jobPriority ?? '') ||
        (newJobSettings.remark ?? '') !== (jobSettings.remark ?? '') ||
        (newJobSettings.preferenceDevice ?? '') !== (jobSettings.preferenceDevice ?? '')
    },
    [EVENT_DIALOG_TAB_KEYS.PIN_SETTING]: () => {
      if (!newPinJobSettings || !pinSettings) {
        return false
      }
      return newPinJobSettings.dateStart !== pinSettings.currentJob.dateStart ||
        newPinJobSettings.pinnedDate !== pinSettings.currentJob.pinnedDate
    }
  }

  const handleTabChange = (tabKey) => (requestedTab) => {
    const dirty = isTabDirty[tabKey]
    if (!dirty || !dirty()) {
      onChangeSelectedTab(requestedTab)
      return
    }
    setshowSaveDialog(true)
    setNextTab(requestedTab)
  }

  const componentByTabKey = {
    [EVENT_DIALOG_TAB_KEYS.JOB_SETTINGS]: (
      <JobSettingsDialogContent
        jobSettings={jobSettings}
        newJobSettings={newJobSettings}
        onChangeNewJobSettings={onChangeNewJobSettings}
        showErrors={showJobSettingsErrors}
      />
    ),
    [EVENT_DIALOG_TAB_KEYS.PART_SETTINGS]: (
      <div>
        <PartDateDialogContent event={event} setEvent={setEvent} />
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '30px' }}>
          <Button
            variant='contained'
            color='primary'
            onClick={handleTempCloseAndChangeColor}
          >
            {t_('Show resources family')}
          </Button>
        </div>
      </div>
    ),
    [EVENT_DIALOG_TAB_KEYS.PIN_SETTING]: (
      <PinJobEventDialog
        pinSettings={pinSettings}
        newJobPinSettings={newPinJobSettings}
        onChangeNewPinJobSettings={onChangeNewPinJobSettings}
      />
    )
  }

  const componentsInfo = EVENT_DIALOG_TABS.map((tab) => ({
    title: t_(tab.label),
    component: componentByTabKey[tab.key],
    onChange: handleTabChange(tab.key)
  }))

  return (
    <div>
      {AlertElementDialog}
      <SmartSaveDialog
        show={showSaveDialog}
        cancelCallback={() => {
          // Discarding restores the values currently stored for the job, for the tab being left --
          // resolved by NAME (S29/S83).
          switch (selectedTabKey) {
            case EVENT_DIALOG_TAB_KEYS.JOB_SETTINGS:
              onChangeNewJobSettings(jobSettings)
              break
            case EVENT_DIALOG_TAB_KEYS.PIN_SETTING:
              onChangeNewPinJobSettings(pinSettings.currentJob)
              break
            default:
              break
          }
          setshowSaveDialog(false)
          onChangeSelectedTab(nextTab)
        }}
        saveCallback={() => {
          onAccept(false)
          setshowSaveDialog(false)
          onChangeSelectedTab(nextTab)
        }}
      />
      <SmartNavigator componentsInfo={componentsInfo} selected={selectedTab} />
    </div>
  )
}

export default EventDialog
